import { CARS, currentProfiles, currentStakes } from './catalog.js';
import { SCENARIOS as V4_SCENARIOS } from './race-stories.js';
import { FLOW_STORIES as SCENARIOS, planBattles } from './flow-stories.js';
import { sampleFlowRace } from './flow-motion.js';
import { sampleStoryRace } from './story-motion.js';
export { FLOW_STORIES as SCENARIOS } from './flow-stories.js';
import { TRACK_LENGTH, STRAIGHT_LENGTH, CURVE_RADIUS, STARTING_GRID } from './track.js';

const narrativeTimes = [
  [2.0, 6.5, 13.8, 22.2, 29.2], [2.4, 7.8, 16.2, 23.8, 29.0],
  [2.3, 6.8, 12.3, 17.4, 22.6], [2.6, 8.5, 14.4, 18.5, 24.4],
  [2.2, 6.4, 13.0, 19.8, 27.3], [2.5, 8.3, 15.1, 24.9, 29.6],
  [2.7, 9.1, 16.9, 24.5, 29.8], [2.1, 7.7, 14.8, 22.8, 29.5],
  [2.4, 8.3, 14.9, 21.9, 29.4], [2.8, 7.5, 15.0, 29.65, 29.99],
  [2.2, 5.9, 12.8, 20.5, 27.3], [2.5, 7.2, 15.4, 24.4, 29.5],
  [2.8, 10.4, 17.3, 24.3, 29.7], [2.3, 7.9, 13.4, 18.7, 23.8],
  [2.6, 8.1, 16.5, 26.7, 29.8], [2.1, 6.7, 13.5, 20.4, 28.6],
  [2.7, 9.2, 15.3, 20.5, 26.8], [2.4, 8.4, 16.7, 25.0, 29.8],
  [2.0, 6.2, 12.9, 21.1, 28.0], [2.6, 8.7, 16.3, 23.9, 29.3],
  [2.3, 6.3, 14.9, 26.8, 29.9], [2.8, 9.3, 18.2, 27.2, 29.85],
  [2.2, 6.7, 13.8, 19.1, 24.6], [2.5, 7.4, 15.7, 22.1, 28.4],
  [2.2, 8.8, 16.1, 26.8, 29.9],
];

const patterns = [
  ['12403 12043 20143 21034 02134', .90],
  ['01234 10234 12034 01243 01234', .85],
  ['12304 21304 21034 20134 02143', .78],
  ['12034 01234 20134 21034 02134', .92],
  ['12304 21304 21034 20314 02314', .82],
  ['31240 13420 14023 10423 01423', 1.12],
  ['12034 21034 20134 12043 01243', .65],
  ['01234 10234 01234 10234 01243', .60],
  ['34120 43120 14230 21430 02143', .98],
  ['12340 21340 21034 20134 02134', .52],
  ['01234 01324 10324 13024 01324', .74],
  ['31024 13024 10324 10243 01243', .80],
  ['31204 13204 13024 10324 01342', 1.20],
  ['12034 21034 21304 20134 02134', .91],
  ['12034 01234 10234 12034 01234', .59],
  ['01234 10234 21034 20134 02134', .83],
  ['31204 01234 10234 01324 01342', 1.02],
  ['12304 13204 31204 31024 03124', .77],
  ['12304 12034 21304 21034 02143', .89],
  ['12304 01234 20134 21034 02143', .63],
  ['21304 12304 01234 10234 01234', .49],
  ['31240 23140 23401 23014 02314', .68],
  ['01234 01324 10324 13024 01342', .95],
  ['32104 23104 12304 10234 01234', 1.30],
  ['12304 01234 21034 12034 01243', .71],
];

export const LEGACY_SCENARIOS = Object.freeze(patterns.map(([orders, gap], index) => Object.freeze({
  id: `r${String(index + 1).padStart(2, '0')}`,
  orders: orders.split(' ').map(order => [...order].map(Number)),
  gap,
  beatTimes: Object.freeze(narrativeTimes[index]),
})));

function generator(seed) {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle(values, random) {
  const result = [...values];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function validateSeed(seed) {
  if (!Number.isInteger(seed) || seed < 0 || seed > 0xffffffff) throw new RangeError('Invalid round seed.');
}

export function createOffer(seed) {
  validateSeed(seed);
  const profiles = {};
  // One drawn chance per car; the profile index travels with it so the garage can label it.
  const shuffled = shuffle(currentProfiles(), generator(seed ^ 0x94379abc));
  CARS.forEach((car, index) => { profiles[car.id] = { ...shuffled[index] }; });
  return { seed, profiles };
}

export function createRound({ seed, selectedCarId, amount, scenarioIndex, gridSeed = (seed ^ 0x51f2e8ab) >>> 0, raceVersion = 6, won = null }) {
  validateSeed(seed);
  validateSeed(gridSeed);
  if (![1, 2, 3, 4, 5, 6].includes(raceVersion)) throw new RangeError('Invalid race version.');
  if (!CARS.some(car => car.id === selectedCarId)) throw new RangeError('Invalid car selection.');
  if (!currentStakes().includes(amount)) throw new RangeError('Invalid coin amount.');
  if (won !== null && typeof won !== 'boolean') throw new RangeError('Invalid outcome.');
  const offer = createOffer(seed);
  const random = generator(seed ^ 0x8b42ef31);
  let draw = random(), winnerId = CARS.at(-1).id;
  for (const car of CARS) {
    draw -= offer.profiles[car.id].chance;
    if (draw < 0) { winnerId = car.id; break; }
  }
  // A round the server drew for keeps its outcome: a win leaves the flag on the picked car,
  // a loss hands it to another car of the field, so every race still ends on a winner.
  if (won === true) winnerId = selectedCarId;
  else if (won === false) winnerId = drawOtherWinner(seed, offer, selectedCarId);
  const roles = [winnerId, ...shuffle(CARS.map(car => car.id).filter(id => id !== winnerId), random)];
  const catalogue = raceVersion >= 5 ? SCENARIOS : raceVersion === 4 ? V4_SCENARIOS : LEGACY_SCENARIOS;
  const index = scenarioIndex ?? Math.floor(random() * catalogue.length);
  if (!Number.isInteger(index) || index < 0 || index >= catalogue.length) throw new RangeError('Invalid scenario.');
  const gridOrder = raceVersion === 1
    ? catalogue[index].orders[0].map(role => roles[role])
    : shuffle(CARS.map(car => car.id), generator(gridSeed));
  return {
    id: `${seed.toString(16)}-${index}`, seed, selectedCarId, amount, winnerId, won,
    scenarioIndex: index, roles, profiles: offer.profiles, raceVersion, gridSeed, gridOrder,
    duration: raceVersion >= 4 ? catalogue[index].duration : 30,
    rewardPreview: Math.round(amount * offer.profiles[selectedCarId].factor),
    finalOrder: raceVersion >= 5
      ? planBattles({ scenarioIndex: index, gridOrder, winnerId, raceVersion }).finalOrder
      : catalogue[index].orders.at(-1).map(role => roles[role]),
  };
}

/**
 * The car that takes the flag when the picked one loses: one of the other four, weighted by
 * the chances the field was offered. Its own stream keeps the rest of the race reproducible.
 */
function drawOtherWinner(seed, offer, selectedCarId) {
  const others = CARS.filter(car => car.id !== selectedCarId);
  const random = generator(seed ^ 0x2f6d1b47);
  const total = others.reduce((sum, car) => sum + offer.profiles[car.id].chance, 0);
  let draw = random() * total;
  for (const car of others) {
    draw -= offer.profiles[car.id].chance;
    if (draw < 0) return car.id;
  }
  return others.at(-1).id;
}

const smooth = value => value * value * (3 - 2 * value);
const gridOffsets = STARTING_GRID.map(slot => slot.distance);
const runningOffsets = [0, -3.6, -8.2, -13.2, -19];
const gridLanes = STARTING_GRID.map(slot => slot.lane);
const runningLanes = [-5.4, 5.4, -2.7, 2.7, 0];
const laneSpreadEnd = 1.9;
const gridMergeEnd = 4;

// Distances are metres and speeds are metres per second. Continuous Hermite
// segments let the entire field brake before the geometric bend and accelerate
// on its exit while preserving the 30-second narrative and exact finish line.
const motionKeys = [
  [0, 0, 0], [4, 65, 32], [6.5, 185, 54],
  [9, STRAIGHT_LENGTH, 24],
  [17.5, STRAIGHT_LENGTH + Math.PI * CURVE_RADIUS, 24],
  [30, TRACK_LENGTH, 60],
];
const finishBeatTimes = Object.freeze([...narrativeTimes[9].slice(0, 3), 29.74, 29.99]);

function baseProgress(time, raceVersion) {
  if (time > 30) {
    const endSpeed = raceVersion >= 3 ? 60 : TRACK_LENGTH / 30 * (0.62 * 1.25 + 0.38 * 2);
    const speed = endSpeed * Math.exp(-(time - 30) / .8);
    return { distance: TRACK_LENGTH + endSpeed * .8 * (1 - Math.exp(-(time - 30) / .8)), speed, acceleration: -speed / .8 };
  }
  if (raceVersion >= 3) {
    let index = 1;
    while (index < motionKeys.length - 1 && time > motionKeys[index][0]) index++;
    const [start, distance, startSpeed] = motionKeys[index - 1];
    const [end, endDistance, endSpeed] = motionKeys[index];
    const duration = end - start, u = (time - start) / duration;
    const travel = endDistance - distance;
    const a = 3 * travel - duration * (2 * startSpeed + endSpeed);
    const b = -2 * travel + duration * (startSpeed + endSpeed);
    return {
      distance: distance + duration * startSpeed * u + a * u * u + b * u ** 3,
      speed: startSpeed + (2 * a * u + 3 * b * u * u) / duration,
      acceleration: (2 * a + 6 * b * u) / (duration * duration),
    };
  }
  const u = time / 30;
  return {
    distance: TRACK_LENGTH * (0.62 * u ** 1.25 + 0.38 * u * u),
    speed: TRACK_LENGTH / 30 * (0.62 * 1.25 * u ** 0.25 + 0.76 * u),
    acceleration: time > 0 ? TRACK_LENGTH / 900 * (.62 * 1.25 * .25 * u ** -.75 + .76) : 0,
  };
}

export function scenarioFor(round) {
  return (round.raceVersion >= 5 ? SCENARIOS : round.raceVersion === 4 ? V4_SCENARIOS : LEGACY_SCENARIOS)[round.scenarioIndex];
}

export function sampleRace(round, elapsedSeconds) {
  if (round.raceVersion >= 5) return sampleFlowRace(round, elapsedSeconds);
  if (round.raceVersion === 4) return sampleStoryRace(round, elapsedSeconds);
  const time = Number.isFinite(elapsedSeconds) ? Math.max(0, elapsedSeconds) : 0;
  const scenario = LEGACY_SCENARIOS[round.scenarioIndex];
  const beatTimes = round.raceVersion >= 3 && round.scenarioIndex === 9 ? finishBeatTimes : scenario.beatTimes;
  const randomizedGrid = round.raceVersion >= 2;
  const base = baseProgress(time, round.raceVersion);
  let previousBeat = 0, nextBeat = 0, blend = 0, blendRate = 0, blendAcceleration = 0;
  for (let index = 1; index < beatTimes.length; index++) {
    const fromTime = index === 1 && randomizedGrid ? gridMergeEnd : beatTimes[index - 1];
    if (time > fromTime) {
      previousBeat = index - 1; nextBeat = index;
      const duration = beatTimes[index] - fromTime;
      const u = Math.min(1, (time - fromTime) / duration);
      blend = smooth(u); blendRate = u < 1 ? 6 * u * (1 - u) / duration : 0;
      blendAcceleration = u < 1 ? 6 * (1 - 2 * u) / (duration * duration) : 0;
    }
  }
  const spread = smooth(Math.min(1, time / laneSpreadEnd));
  const gridMerge = Math.max(0, Math.min(1, (time - laneSpreadEnd) / (gridMergeEnd - laneSpreadEnd)));
  const cars = CARS.map(car => {
    const role = round.roles.indexOf(car.id);
    const initialRank = scenario.orders[0].indexOf(role);
    const gridRank = randomizedGrid ? round.gridOrder.indexOf(car.id) : initialRank;
    const fromRank = scenario.orders[previousBeat].indexOf(role);
    const toRank = scenario.orders[nextBeat].indexOf(role);
    const fromOffset = previousBeat === 0 ? gridOffsets[fromRank] : runningOffsets[fromRank] * scenario.gap;
    const toOffset = nextBeat === 0 ? gridOffsets[toRank] : runningOffsets[toRank] * scenario.gap;
    let offset = fromOffset + (toOffset - fromOffset) * blend;
    let offsetSpeed = (toOffset - fromOffset) * blendRate;
    let offsetAcceleration = (toOffset - fromOffset) * blendAcceleration;
    if (randomizedGrid && time <= gridMergeEnd) {
      // Keep each row's longitudinal clearance until every car has a separate lane.
      const change = gridOffsets[initialRank] - gridOffsets[gridRank];
      offset = gridOffsets[gridRank] + change * smooth(gridMerge);
      offsetSpeed = change * 6 * gridMerge * (1 - gridMerge) / (gridMergeEnd - laneSpreadEnd);
      offsetAcceleration = time > laneSpreadEnd && time < gridMergeEnd ? change * 6 * (1 - 2 * gridMerge) / (gridMergeEnd - laneSpreadEnd) ** 2 : 0;
    }
    const wave = Math.sin(time * 0.39 + role * 1.7 + round.scenarioIndex) * 0.22 * spread;
    return {
      id: car.id,
      distance: base.distance + offset,
      lane: gridLanes[gridRank] + (runningLanes[gridRank] - gridLanes[gridRank]) * spread + wave,
      speed: Math.max(0, base.speed + offsetSpeed),
      acceleration: base.acceleration + offsetAcceleration,
      rank: 0,
    };
  });
  const order = [...cars].sort((a, b) => b.distance - a.distance || a.id.localeCompare(b.id)).map(car => car.id);
  for (const car of cars) car.rank = order.indexOf(car.id) + 1;
  const leadDistance = cars.find(car => car.id === order[0]).distance;
  const curveEnd = STRAIGHT_LENGTH + Math.PI * CURVE_RADIUS;
  const phase = time >= 30 ? 'finished' : leadDistance > TRACK_LENGTH - 210 ? 'sprint' : leadDistance > STRAIGHT_LENGTH && leadDistance < curveEnd ? 'corner' : time < 7 ? 'launch' : 'straight';
  const braking = round.raceVersion >= 3 && time < 12 && base.acceleration < -2;
  const accelerating = round.raceVersion >= 3 && leadDistance >= curveEnd && leadDistance < curveEnd + 160;
  return { cars, order, phase, braking, accelerating, leader: order[0], finished: time >= 30 };
}

export function settlementFor(round) {
  const selectedRank = round.finalOrder.indexOf(round.selectedCarId) + 1;
  const reward = selectedRank === 1 ? round.rewardPreview : 0;
  return { winnerId: round.winnerId, selectedRank, amount: round.amount, reward, net: reward - round.amount };
}
